import React, { useState, useEffect } from "react";
import { MessageSquare, Heart, AlertCircle, Plus, HeartHandshake } from "lucide-react";
import axios from "axios";

export default function PrayerWall({ user, onOpenAuth }) {
  const [prayers, setPrayers] = useState([]);
  const [title, setTitle] = useState("");
  const [request, setRequest] = useState("");
  const [author, setAuthor] = useState(user?.name || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPrayers = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/spiritual/prayer-requests`);
      setPrayers(data);
    } catch (err) {
      console.error("Failed to fetch prayers", err);
    }
  };

  useEffect(() => {
    fetchPrayers();
  }, []);

  const handleSubmitPrayer = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/spiritual/prayer-requests`, {
        title,
        request,
        author: author.trim() || "Anonymous"
      });

      setSuccess("Your prayer request has been shared with the SSA community!");
      setTitle("");
      setRequest("");
      setAuthor(user?.name || "");
      fetchPrayers();
    } catch (err) {
      console.error(err);
      setError("Failed to share your prayer request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAmen = async (id) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Post to amen endpoint (with credentials if authenticated, otherwise works anonymously with fallback)
      const { data } = await axios.post(
        `${API_URL}/api/spiritual/prayer-requests/${id}/amen`,
        {},
        { withCredentials: true }
      );

      // Local state toggle update to feel incredibly responsive and smooth
      setPrayers((prevPrayers) =>
        prevPrayers.map((p) => {
          if (p.id === id) {
            return {
              ...p,
              amens: data.amens,
              amen_users: p.amen_users
                ? user && p.amen_users.includes(user.id)
                  ? p.amen_users.filter((uid) => uid !== user.id)
                  : user
                  ? [...p.amen_users, user.id]
                  : p.amen_users
                : []
            };
          }
          return p;
        })
      );
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        setError("Please log in to lift others in prayer.");
        if (onOpenAuth) onOpenAuth();
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 font-manrope text-white">
      {/* Page Title & Motivation */}
      <div className="text-center mb-16 space-y-4">
        <div className="flex justify-center">
          <div className="border border-white p-2">
            <HeartHandshake className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl tracking-wider uppercase leading-none">
          Prayer Wall
        </h1>
        <p className="text-sm uppercase tracking-widest text-neutral-400 max-w-xl mx-auto leading-relaxed">
          "For where two or three are gathered in my name, there am I among them."
          <span className="block text-xs mt-2 text-neutral-500 font-bold">— Matthew 18:20</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column: Share a Prayer Request Form */}
        <div className="lg:col-span-5 space-y-8">
          <div className="border border-neutral-800 p-8 bg-neutral-950/40 relative">
            <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-3 py-1 font-bebas text-xs text-neutral-500">
              STESTAMENT
            </div>
            
            <h3 className="font-bebas text-2xl uppercase tracking-wider mb-6">
              Share Your Burden
            </h3>

            {error && (
              <div 
                className="mb-4 bg-white text-black px-4 py-3 text-xs uppercase tracking-wider font-semibold flex items-center gap-2"
                data-testid="prayer-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div 
                className="mb-4 border border-white text-white px-4 py-3 text-xs uppercase tracking-wider font-semibold"
                data-testid="prayer-success"
              >
                {success}
              </div>
            )}

            <form onSubmit={handleSubmitPrayer} className="space-y-5">
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                  Prayer Title
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Endurance in Trial"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0"
                  data-testid="prayer-title-input"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                  How can we pray for you?
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Share your spiritual or physical health trials, family needs, or goals so the SSA community can join you in prayer..."
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                  className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0 resize-none"
                  data-testid="prayer-text-input"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                  Your Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Elijah or leave blank for Anonymous"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0"
                  data-testid="prayer-author-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-2"
                data-testid="prayer-submit-button"
              >
                <Plus className="w-4 h-4" />
                <span>{loading ? "Sharing..." : "Post to Prayer Wall"}</span>
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: Rendered Prayers Feed */}
        <div className="lg:col-span-7 space-y-6">
          <div className="flex justify-between items-center border-b border-neutral-800 pb-4">
            <span className="text-xs uppercase tracking-widest text-neutral-400 font-semibold font-mono">
              Community Intentions ({prayers.length})
            </span>
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
              Live Updates
            </span>
          </div>

          {prayers.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-neutral-800">
              <MessageSquare className="w-8 h-8 text-neutral-700 mx-auto mb-3" />
              <p className="text-sm text-neutral-500 uppercase tracking-widest">
                No prayers posted yet. Be the first to share.
              </p>
            </div>
          ) : (
            <div className="space-y-6 max-h-[700px] overflow-y-auto pr-2" data-testid="prayer-requests-list">
              {prayers.map((p) => {
                const alreadyAmened = user && p.amen_users?.includes(user.id);
                return (
                  <div
                    key={p.id}
                    className="border border-neutral-800 p-6 bg-neutral-950/20 hover:border-neutral-700 transition-all"
                    data-testid={`prayer-item-${p.id}`}
                  >
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <div>
                        <h4 className="font-bebas text-xl tracking-wide uppercase">{p.title}</h4>
                        <span className="text-[10px] text-neutral-500 uppercase tracking-widest">
                          By {p.author} • {p.created_at ? new Date(p.created_at).toLocaleDateString() : "Today"}
                        </span>
                      </div>
                      
                      {/* Amen Button */}
                      <button
                        onClick={() => handleAmen(p.id)}
                        className={`flex items-center gap-2 border px-3 py-1.5 text-xs uppercase tracking-widest transition-all focus:outline-none ${
                          alreadyAmened
                            ? "bg-white text-black border-white font-extrabold"
                            : "bg-transparent border-neutral-800 text-neutral-400 hover:border-neutral-500 hover:text-white"
                        }`}
                        data-testid={`amen-button-${p.id}`}
                      >
                        <Heart className={`w-3.5 h-3.5 ${alreadyAmened ? "fill-black" : "fill-transparent"}`} />
                        <span>Amen ({p.amens || 0})</span>
                      </button>
                    </div>

                    <p className="text-sm text-neutral-300 leading-relaxed font-manrope font-light whitespace-pre-line">
                      {p.request}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
